import React from 'react';
import { useTranslation } from 'react-i18next';

const STATUS_STYLES = {
    loaded:     { color: '#0891b2', bg: '#ecfeff' },
    in_transit: { color: '#3b82f6', bg: '#eff6ff' },
    delivered:  { color: '#059669', bg: '#ecfdf5' },
    unloaded:   { color: '#059669', bg: '#ecfdf5' },
    pending:    { color: '#d97706', bg: '#fffbeb' },
    default:    { color: '#6b7280', bg: '#f3f4f6' },
};

const StatusBadge = ({ status }) => {
    const s = STATUS_STYLES[status?.toLowerCase()] || STATUS_STYLES.default;
    return (
        <span style={{
            fontSize: 11, fontWeight: 700,
            color: s.color, backgroundColor: s.bg,
            borderRadius: 12, padding: '3px 9px',
            textTransform: 'capitalize',
        }}>
            {status?.replace(/_/g, ' ') || '—'}
        </span>
    );
};

const Cell = ({ children, align = 'left', muted }) => (
    <td style={{
        padding: '10px 14px',
        fontSize: 13,
        color: muted ? '#9ca3af' : '#374151',
        fontWeight: muted ? 400 : 500,
        textAlign: align,
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children ?? '—'}
    </td>
);

const Th = ({ children, align = 'left' }) => (
    <th style={{
        padding: '10px 14px',
        fontSize: 11, fontWeight: 700,
        color: '#9ca3af', textTransform: 'uppercase',
        letterSpacing: '0.4px', textAlign: align,
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const LoadingTable = ({ items }) => {
    const { t } = useTranslation();
    return (
    <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
                <tr>
                    <Th>#</Th>
                    <Th>{t('common.analyticsColumns.subOrder')}</Th>
                    <Th>{t('common.analyticsColumns.orderNo')}</Th>
                    <Th>{t('common.analyticsColumns.customer')}</Th>
                    <Th>{t('common.analyticsColumns.tallyName')}</Th>
                    <Th>{t('common.analyticsColumns.challan')}</Th>
                    <Th>{t('common.analyticsColumns.vehicle')}</Th>
                    <Th>{t('common.analyticsColumns.status')}</Th>
                    <Th align="right">{t('common.analyticsColumns.weightT')}</Th>
                    <Th>{t('common.analyticsColumns.date')}</Th>
                </tr>
            </thead>
            <tbody>
                {items.map((row, i) => (
                    <tr key={row.sub_order_number || i}
                        style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa' }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f9ff')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#ffffff' : '#fafafa')}
                    >
                        <Cell muted>{i + 1}</Cell>
                        <Cell>{row.sub_order_number}</Cell>
                        <Cell>{row.order_number}</Cell>
                        <Cell>{row.customer_name}</Cell>
                        <Cell>{row.tally_name}</Cell>
                        <Cell>{row.challan_number}</Cell>
                        <Cell>{row.vehicle_number}</Cell>
                        <Cell><StatusBadge status={row.status} /></Cell>
                        <Cell align="right">
                            {row.total_weight != null
                                ? `${Number(row.total_weight).toLocaleString()} kg`
                                : '—'}
                        </Cell>
                        <Cell muted>{row.date || row.loading_date || row.created_at?.split('T')[0]}</Cell>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
    );
};

const UnloadingTable = ({ items }) => {
    const { t } = useTranslation();
    return (
    <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
                <tr>
                    <Th>#</Th>
                    <Th>{t('common.analyticsColumns.adviceNo')}</Th>
                    <Th>{t('common.analyticsColumns.poNumber')}</Th>
                    <Th>{t('common.analyticsColumns.supplier')}</Th>
                    <Th>{t('common.analyticsColumns.challan')}</Th>
                    <Th>{t('common.analyticsColumns.vehicle')}</Th>
                    <Th>{t('common.analyticsColumns.status')}</Th>
                    <Th align="right">{t('common.analyticsColumns.weightT')}</Th>
                    <Th>{t('common.analyticsColumns.date')}</Th>
                    <Th>{t('common.analyticsColumns.remark')}</Th>
                </tr>
            </thead>
            <tbody>
                {items.map((row, i) => (
                    <tr key={row.advice_number || i}
                        style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa' }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fdf4ff')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#ffffff' : '#fafafa')}
                    >
                        <Cell muted>{i + 1}</Cell>
                        <Cell>{row.advice_number}</Cell>
                        <Cell>{row.po_number}</Cell>
                        <Cell>{row.supplier_name}</Cell>
                        <Cell>{row.challan_number}</Cell>
                        <Cell>{row.vehicle_number}</Cell>
                        <Cell><StatusBadge status={row.status} /></Cell>
                        <Cell align="right">
                            {row.loaded_truck_weight != null && row.empty_truck_weight != null
                                ? `${Number(row.loaded_truck_weight - row.empty_truck_weight).toLocaleString()} kg`
                                : row.truck_weight != null
                                    ? `${Number(row.truck_weight).toLocaleString()} kg`
                                    : '—'}
                        </Cell>
                        <Cell muted>{row.date || row.unloading_date || row.created_at?.split('T')[0]}</Cell>
                        <Cell muted>{row.remark}</Cell>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
    );
};

const SupervisorActivityTable = ({ type, items, loading }) => {
    return (
        <div style={cardStyle}>
            {loading && (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    Loading data...
                </div>
            )}
            {!loading && !items?.length && (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    No records found for the selected filters.
                </div>
            )}
            {!loading && items?.length > 0 && (
                type === 'loading'
                    ? <LoadingTable items={items} />
                    : <UnloadingTable items={items} />
            )}
        </div>
    );
};

const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};

export default SupervisorActivityTable;
